/**
 * Sidebar theme builder: phát sau khi panel form (theo `option.element`) đã commit vào DOM
 * (thay cho chuỗi requestAnimationFrame không đồng bộ).
 */
export const THEME_SIDEBAR_PANEL_READY = 'fd-theme-sidebar-panel-ready'

/** Thời lượng CSS transition L1↔L2 (ThemeBuilder.sidebar_track). */
export const SIDEBAR_L1_L2_SLIDE_MS = 280

export type ThemeSidebarPanelReadyDetail = {
  /** Khớp `edit_theme.option.element` / NavButton.element (vd. BrandStyle, LineItem). */
  element: string
}

export function dispatchThemeSidebarPanelReady(
  detail: ThemeSidebarPanelReadyDetail,
): void {
  if (typeof document === 'undefined') return
  document.dispatchEvent(
    new CustomEvent(THEME_SIDEBAR_PANEL_READY, {
      detail,
      bubbles: true,
    }),
  )
}

const DEFAULT_FALLBACK_MS = 4000

/**
 * Đợi panel đúng `expectedElement` báo ready (CustomEvent), rồi (tuỳ chọn) settle
 * thêm `settleMs` — dùng khi L1→L2 vừa animate, tránh mở L3 giữa chừng → lệch.
 * Trả về hàm hủy (gỡ listener / clear timer, không gọi onReady).
 */
export function waitForSidebarPanelReady(
  expectedElement: string,
  onReady: () => void,
  options?: { fallbackMs?: number; settleMs?: number },
): () => void {
  const fallbackMs = options?.fallbackMs ?? DEFAULT_FALLBACK_MS
  const settleMs = options?.settleMs ?? 0
  let finished = false
  let settleTimer: number | null = null

  const cleanupListeners = () => {
    clearTimeout(fallbackTimer)
    if (settleTimer != null) {
      window.clearTimeout(settleTimer)
      settleTimer = null
    }
    document.removeEventListener(THEME_SIDEBAR_PANEL_READY, onPanelReady)
  }

  const invokeReady = () => {
    if (finished) return
    if (settleMs > 0) {
      // Panel đã mount — đợi L1→L2 transition xong rồi mới deep-link L3.
      document.removeEventListener(THEME_SIDEBAR_PANEL_READY, onPanelReady)
      clearTimeout(fallbackTimer)
      settleTimer = window.setTimeout(() => {
        settleTimer = null
        if (finished) return
        finished = true
        onReady()
      }, settleMs)
      return
    }
    finished = true
    cleanupListeners()
    onReady()
  }

  const onPanelReady = (ev: Event) => {
    const d = (ev as CustomEvent<ThemeSidebarPanelReadyDetail>).detail
    if (d?.element === expectedElement) {
      invokeReady()
    }
  }

  const fallbackTimer = window.setTimeout(invokeReady, fallbackMs)
  document.addEventListener(THEME_SIDEBAR_PANEL_READY, onPanelReady)

  return () => {
    if (finished) return
    finished = true
    cleanupListeners()
  }
}

/**
 * CollapsibleList form: phát khi một section (id = `config.action`) vừa mở.
 * `useComponentConfigTheme` lắng nghe để biết layout #scrollTo sẵn sàng thay vì MutationObserver.
 */
export const THEME_COLLAPSIBLE_SECTION_OPENED =
  'fd-theme-collapsible-section-opened'

export type ThemeCollapsibleSectionOpenedDetail = {
  /** Khớp `CollapsibleList.Item` `id` — cùng giá trị `action` trong `SettingComponentMap`. */
  sectionId: string
}

export function dispatchThemeCollapsibleSectionOpened(
  detail: ThemeCollapsibleSectionOpenedDetail,
): void {
  if (typeof document === 'undefined') return
  document.dispatchEvent(
    new CustomEvent(THEME_COLLAPSIBLE_SECTION_OPENED, {
      detail,
      bubbles: true,
    }),
  )
}
